import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Mail, ArrowRight } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';

const AssistantsCheckoutSuccess: React.FC = () => {
  return (
    <>
      <Navbar />
      <main className="pt-16 min-h-screen bg-background">
        <section className="py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto text-center">
              {/* Success Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-brand-teal/10 rounded-full mb-8">
                <CheckCircle className="h-10 w-10 text-brand-teal" />
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 font-space">
                Welcome Aboard!
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8">
                Your Advisory Assistant subscription is now active. 
                We're excited to help you transform your client experience!
              </p>

              {/* Email Notice */}
              <div className="bg-muted/50 border border-border rounded-xl p-8 mb-8 text-left">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <Mail className="h-8 w-8 text-brand-teal" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">
                      Check Your Email
                    </h2>
                    <p className="text-muted-foreground mb-4">
                      We've sent you a secure magic link to access your onboarding dashboard. 
                      No password required – just click the link in the email!
                    </p>
                    <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4">
                      <p className="text-sm text-card-foreground">
                        <strong>📧 Didn't receive the email?</strong><br/>
                        Check your spam/junk folder. The email is from <span className="font-mono text-brand-teal">noreply@thinktomorrow.ai</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* What's Next */}
              <div className="bg-card border border-border rounded-xl p-8 mb-8 text-left">
                <h3 className="text-lg font-semibold text-card-foreground mb-4">What Happens Next?</h3>
                <ol className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-brand-teal/10 rounded-full flex items-center justify-center text-sm font-bold text-brand-teal">1</span>
                    <div>
                      <p className="text-card-foreground font-medium">Click the magic link in your email</p>
                      <p className="text-sm text-muted-foreground">Secure, passwordless access to your dashboard</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-brand-teal/10 rounded-full flex items-center justify-center text-sm font-bold text-brand-teal">2</span>
                    <div>
                      <p className="text-card-foreground font-medium">Complete the onboarding wizard</p>
                      <p className="text-sm text-muted-foreground">Upload your FAQs, documents, and brand assets</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-brand-teal/10 rounded-full flex items-center justify-center text-sm font-bold text-brand-teal">3</span>
                    <div>
                      <p className="text-card-foreground font-medium">Our team configures your assistant</p>
                      <p className="text-sm text-muted-foreground">Usually within 7-14 days</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-brand-teal/10 rounded-full flex items-center justify-center text-sm font-bold text-brand-teal">4</span>
                    <div>
                      <p className="text-card-foreground font-medium">Preview and approve</p>
                      <p className="text-sm text-muted-foreground">Test your assistant before going live</p>
                    </div>
                  </li>
                </ol>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/assistants">
                  <Button variant="outline" size="lg">
                    Back to Advisory Assistants
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg">
                    Questions? Contact Us
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>

              <p className="text-sm text-muted-foreground mt-8">
                Need help? Email us at{' '}
                <a href="mailto:connect@thinktomorrow.ai" className="text-brand-teal hover:underline">
                  connect@thinktomorrow.ai
                </a>
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AssistantsCheckoutSuccess;
